/**
 * Token Expiry Handler Utility
 * 
 * Handles automatic logout at token expiry with audit logging.
 * - setupTokenExpiryHandler: Called on fresh login to log token info and set up auto-logout timer
 * - restoreTokenExpiryTimer: Called on page refresh to re-establish the timer without logging
 */

import { writeAuditLog } from '../components/common/apis';
import { clearStockerUserFlag } from './stockerNavPermissions';
import { clearStoreLocationOptions } from '../components/grid/storeLocationFilter';

const SIX_HOURS_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

/**
 * Persists account-level auto-logout setting for the current session.
 * @param {object|boolean|null|undefined} accountOrValue - Account object or boolean
 */
export const persistAutoLogoutAccountSetting = (accountOrValue) => {
  let enabled = false;
  if (typeof accountOrValue === 'boolean') {
    enabled = accountOrValue;
  } else if (accountOrValue && typeof accountOrValue === 'object') {
    const raw = accountOrValue.is_auto_logout_enabled;
    enabled = raw === true || raw === 'true';
  }
  sessionStorage.setItem('is_auto_logout_enabled', enabled ? 'true' : 'false');
};

/** Reads account-level auto-logout setting from sessionStorage (default: false). */
export const isAutoLogoutEnabled = () =>
  [true, 'true'].includes(sessionStorage.getItem('is_auto_logout_enabled'));

/**
 * Gets session user and token the same way writeAuditLog and API calls do.
 * Single source of truth - ensures token expiry check uses identical token as audit/API.
 * Token path: sessionStorage.user.signInUserSession.idToken.jwtToken
 */
const getSessionUserAndToken = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    if (!userStr || userStr === '{}') return { user: null, token: null };
    const user = JSON.parse(userStr);
    const token = user && user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.jwtToken;
    return { user, token: token || null };
  } catch (e) {
    return { user: null, token: null };
  }
};

const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * Decodes a JWT token to extract its payload
 * @param {string} token - JWT token string
 * @returns {object|null} - Decoded payload or null if invalid
 */
const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Failed to decode JWT payload:', error);
    return null;
  }
};

/**
 * Normalizes exp value to Unix seconds. Handles ms (values > 1e12) and string numbers.
 * @param {*} raw - Raw exp value from token
 * @returns {number|null} - Expiry in Unix seconds, or null
 */
const normalizeExpToSeconds = (raw) => {
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : (typeof raw === 'string' ? parseInt(raw, 10) : NaN);
  if (!Number.isFinite(n)) return null;
  // Values > 1e12 are likely milliseconds (e.g. Date.now())
  return n > 1e12 ? Math.floor(n / 1000) : n;
};

/**
 * Extracts token expiry and issued-at from user object
 * @param {object} user - User object from sessionStorage
 * @returns {object} - { exp, iat } in seconds (Unix timestamp)
 */
export const extractTokenInfo = (user) => {
  let exp = null;
  let iat = null;

  try {
    const jwtToken = user && user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.jwtToken;
    const payload = user && user.signInUserSession && user.signInUserSession.idToken && user.signInUserSession.idToken.payload;

    // Prefer decoding the JWT when available (source of truth for exp/iat in standard seconds).
    // SAML/OIDC backends may put claims in payload with different exp format (e.g. ms or string),
    // which can make the token appear expired and trigger instant logout.
    if (jwtToken && typeof jwtToken === 'string') {
      const decoded = decodeJwtPayload(jwtToken);
      if (decoded) {
        exp = normalizeExpToSeconds(decoded.exp);
        if (exp != null) {
          iat = normalizeExpToSeconds(decoded.iat);
          return { exp, iat };
        }
      }
    }

    // Fallback: payload (Cognito-style or when JWT decode failed)
    const payloadExp = payload && (payload.exp != null ? payload.exp : payload.expiration != null ? payload.expiration : payload.expires_at);
    exp = normalizeExpToSeconds(payloadExp);
    if (exp != null) {
      iat = normalizeExpToSeconds(payload && (payload.iat != null ? payload.iat : payload.issued_at));
    }
  } catch (error) {
    console.warn('Failed to extract token info:', error);
  }

  return { exp, iat };
};

/**
 * Formats time duration in hours and minutes
 * @param {number} ms - Duration in milliseconds
 * @returns {string} - Formatted string like "8h 30m"
 */
const formatDuration = (ms) => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

/**
 * Performs the auto-logout operation
 * Writes audit log and redirects to login
 */
const performAutoLogout = async () => {
  console.log('⏰ [TokenExpiry] Auto-logout triggered due to token expiry');

  // Capture token source and expiry before clearing session
  const { user, token } = getSessionUserAndToken();
  const tokenSource = 'sessionStorage.user.signInUserSession.idToken.jwtToken';
  const { exp } = user ? extractTokenInfo(user) : { exp: null };
  const tokenExpiryMetadata = {
    tokenSource,
    hadToken: !!(token && typeof token === 'string'),
    tokenExpiryUnix: exp,
    tokenExpiryIso: exp != null ? new Date(exp * 1000).toISOString() : null,
  };

  const autoLogoutEnabled = isAutoLogoutEnabled();

  try {
    // Write audit log for system-initiated logout with enhanced metadata
    await writeAuditLog(
      autoLogoutEnabled
        ? 'System logged out user automatically due to token expiry.'
        : 'Token expiry would trigger auto-logout (is_auto_logout_enabled=false, user not logged out).',
      'Logout',
      {},
      {
        eventType: 'TOKEN_EXPIRED_AUTO_LOGOUT',
        severity: 'INFO',
        metadata: {
          actor: 'SYSTEM',
          reason: 'TOKEN_EXPIRY',
          tokenExpiredAt: new Date().toISOString(),
          hadActiveSocket: (window.socket && window.socket.connected) || false,
          lastActivityTime: localStorage.getItem('lastActivityTime') || null,
          autoLogoutSkipped: !autoLogoutEnabled,
          accountAutoLogoutEnabled: autoLogoutEnabled,
          ...tokenExpiryMetadata,
        },
      },
      { deferMs: 0, wait: true }
    );
  } catch (error) {
    console.error('Failed to write auto-logout audit log:', error);
  }

  if (!autoLogoutEnabled) {
    console.log('⏰ [TokenExpiry] Auto-logout skipped (is_auto_logout_enabled=false for account)');
    return;
  }

  // Clear session storage
  sessionStorage.setItem('user', JSON.stringify({}));
  sessionStorage.setItem('permissions', JSON.stringify([]));
  sessionStorage.removeItem('roleId');
  clearStockerUserFlag();
  clearStoreLocationOptions();

  // Clear the timeout reference
  window.tokenExpiryTimeout = null;

  // Redirect to login
  window.location.href = '/';
};

/**
 * Sets up the auto-logout timer and warning timer
 * @param {number} timeUntilExpiry - Time until expiry in milliseconds
 */
const setupAutoLogoutTimer = (timeUntilExpiry) => {
  // Clear any existing timeouts
  if (window.tokenExpiryTimeout) {
    clearTimeout(window.tokenExpiryTimeout);
    window.tokenExpiryTimeout = null;
  }
  if (window.tokenExpiryWarningTimeout) {
    clearTimeout(window.tokenExpiryWarningTimeout);
    window.tokenExpiryWarningTimeout = null;
  }

  // Only set timer if expiry is more than 6 hours away
  if (timeUntilExpiry > SIX_HOURS_MS) {
    console.log(`⏰ [TokenExpiry] Setting auto-logout timer for ${formatDuration(timeUntilExpiry)}`);
    window.tokenExpiryTimeout = setTimeout(performAutoLogout, timeUntilExpiry);
    
    // Set warning timer (5 minutes before expiry)
    if (timeUntilExpiry > WARNING_BEFORE_EXPIRY_MS) {
      const warningTime = timeUntilExpiry - WARNING_BEFORE_EXPIRY_MS;
      console.log(`⏰ [TokenExpiry] Setting warning timer for ${formatDuration(warningTime)} (5 min before expiry)`);
      window.tokenExpiryWarningTimeout = setTimeout(() => {
        console.log('⏰ [TokenExpiry] Warning: Session will expire in 5 minutes');
        window.dispatchEvent(new CustomEvent('tokenExpiryWarning', {
          detail: { minutesRemaining: 5 }
        }));
      }, warningTime);
    }
  } else {
    console.log(`⏰ [TokenExpiry] Token expiry (${formatDuration(timeUntilExpiry)}) is <= 6 hours, skipping auto-logout timer`);
  }
};

/**
 * Sets up token expiry handling on fresh login
 * Writes audit log with token info and sets up auto-logout timer if needed
 * Uses getSessionUserAndToken() when available - same source as API/audit calls
 * 
 * @param {object} user - User object (fallback when sessionStorage not yet populated)
 * @param {object} options - Optional configuration
 * @param {string} options.loginMethod - Login method (STANDARD, SSO, etc.)
 */
export const setupTokenExpiryHandler = async (user, options = {}) => {
  // Prefer sessionStorage - same source as writeAuditLog and API calls
  const { user: sessionUser, token } = getSessionUserAndToken();
  const userToCheck = (sessionUser && token) ? sessionUser : user;

  if (!userToCheck) {
    console.warn('⏰ [TokenExpiry] No user object provided, skipping token expiry setup');
    return;
  }

  const { exp, iat } = extractTokenInfo(userToCheck);

  if (!exp) {
    console.warn('⏰ [TokenExpiry] No expiry found in token, skipping token expiry setup');
    return;
  }

  const expiryMs = exp * 1000;
  const issuedAtMs = iat ? iat * 1000 : null;
  const currentTime = Date.now();
  const timeUntilExpiry = expiryMs - currentTime;

  // Token already expired
  if (timeUntilExpiry <= 0) {
    console.warn('⏰ [TokenExpiry] Token is already expired');
    return;
  }

  const expiryDate = new Date(expiryMs).toISOString();
  const issuedAtDate = issuedAtMs ? new Date(issuedAtMs).toISOString() : 'N/A';
  const loginMethod = options.loginMethod || sessionStorage.getItem('loginMethod') || 'UNKNOWN';

  // Write audit log with token info
  try {
    const auditMessage = `Token acquired. Expiry: ${expiryDate}, Issued: ${issuedAtDate}, Valid for: ${formatDuration(timeUntilExpiry)}`;
    
    await writeAuditLog(
      auditMessage,
      'Login',
      {},
      {
        eventType: 'TOKEN_ACQUIRED',
        severity: 'INFO',
        metadata: {
          tokenExpiry: expiryDate,
          tokenIssuedAt: issuedAtDate,
          tokenValidityMs: timeUntilExpiry,
          loginMethod: loginMethod,
        },
      },
      { deferMs: 0, wait: false }
    );
    
    console.log(`⏰ [TokenExpiry] Token info logged - Expiry: ${expiryDate}, Valid for: ${formatDuration(timeUntilExpiry)}`);
  } catch (error) {
    console.error('Failed to write token acquired audit log:', error);
  }

  // Set up auto-logout timer
  setupAutoLogoutTimer(timeUntilExpiry);
};

/**
 * Restores the token expiry timer on page refresh
 * Does NOT write audit log (token was already logged on initial login)
 * Reads token from sessionStorage and re-establishes the timer if needed
 */
export const restoreTokenExpiryTimer = () => {
  // Skip if timer is already set
  if (window.tokenExpiryTimeout) {
    console.log('⏰ [TokenExpiry] Timer already exists, skipping restore');
    return;
  }

  // Get user from sessionStorage - same source as API/audit calls
  const { user, token } = getSessionUserAndToken();
  if (!user || !token) {
    return;
  }

  const { exp } = extractTokenInfo(user);

  if (!exp) {
    console.warn('⏰ [TokenExpiry] No expiry found in token during restore');
    return;
  }

  const expiryMs = exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiry = expiryMs - currentTime;

  // Token already expired - trigger immediate logout
  if (timeUntilExpiry <= 0) {
    console.warn('⏰ [TokenExpiry] Token is already expired on page refresh, triggering logout');
    performAutoLogout();
    return;
  }

  console.log(`⏰ [TokenExpiry] Restoring timer on page refresh - ${formatDuration(timeUntilExpiry)} until expiry`);

  // Set up auto-logout timer (only if > 6 hours)
  setupAutoLogoutTimer(timeUntilExpiry);
};

/**
 * Clears the token expiry timeout and warning timeout
 * Should be called during manual logout
 */
export const clearTokenExpiryTimeout = () => {
  if (window.tokenExpiryTimeout) {
    console.log('⏰ [TokenExpiry] Clearing token expiry timeout');
    clearTimeout(window.tokenExpiryTimeout);
    window.tokenExpiryTimeout = null;
  }
  if (window.tokenExpiryWarningTimeout) {
    console.log('⏰ [TokenExpiry] Clearing token expiry warning timeout');
    clearTimeout(window.tokenExpiryWarningTimeout);
    window.tokenExpiryWarningTimeout = null;
  }
};

/**
 * Writes an audit log with token expiry validation.
 * If the token has expired, triggers logout instead of writing the audit log.
 * 
 * @param {string} description - Audit log description
 * @param {string} action - Audit action name
 * @param {object} obj - Additional object data (userId, parentAccountId, boxId, etc.)
 * @param {object} auditContext - Audit context with eventType, severity, metadata
 * @param {object} options - Options like deferMs and wait
 * @returns {object} - { loggedOut: boolean } indicating if user was logged out due to token expiry
 */
export const writeAuditLogWithTokenCheck = async (description, action, obj = {}, auditContext = {}, options = {}) => {
  // Get user/token from sessionStorage - same source as writeAuditLog and API calls
  const { user } = getSessionUserAndToken();
  if (!user) {
    return { loggedOut: false };
  }

  const { exp } = extractTokenInfo(user);

  // Only logout when we have a definite expired token. If exp is null (can't determine),
  // don't assume expired - SAML/OIDC tokens may use formats we don't parse; proceed with
  // audit log and let the API return 401 if the token is actually invalid.
  if (exp !== null && exp !== undefined && (exp * 1000) <= Date.now()) {
    console.warn('⏰ [TokenExpiry] Token expired, triggering logout');
    await performAutoLogout();
    return { loggedOut: true };
  }

  // Token valid - write audit log
  try {
    await writeAuditLog(description, action, obj, auditContext, options);
    console.log(`📝 [Audit] Successfully logged: ${description}`);
  } catch (error) {
    console.error('📝 [Audit] Failed to write audit log:', error);
  }
  
  return { loggedOut: false };
};



// WEBPACK FOOTER //
// ./src/utils/tokenExpiryHandler.js